import React from 'react';
import { Button } from './ui/button';
import { ThemeToggle } from './ThemeToggle';
import { UIScaleToggle } from './UIScaleToggle';
import { OnlineUsers } from './OnlineUsers';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  LogOut,
  Settings,
  User,
  CreditCard,
  Users,
  Plus,
  QrCode,
  FlaskConical
} from 'lucide-react';

interface MobileHeaderProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export const MobileHeader: React.FC<MobileHeaderProps> = ({
  onTabChange
}) => {
  const { user, logout, currentLab } = useAuth();

  const isOwner = currentLab?.ownerId === user?.uid;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const getUserInitials = (email: string) => {
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  return (
    <header className="bg-background border-b sticky top-0 z-50">
      <div className="flex items-center justify-between p-4">
        {/* Left side - Logo and Title */}
        <div className="flex items-center space-x-3 flex-1">
          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
            CL
          </div>
        </div>

        {/* Right side - Controls and Menu */}
        <div className="flex items-center space-x-2">
          {/* Accessibility Quick Toggle - Prominent for better visibility */}
          <div className="flex items-center bg-muted/50 rounded-lg p-1">
            <UIScaleToggle />
          </div>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                    {user?.email ? getUserInitials(user.email) : "AD"}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{currentLab?.name || "Lab Admin"}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email || "admin@gc-pathological.com"}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* Accessibility Section */}
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Accessibility
              </DropdownMenuLabel>
              <div className="px-2 py-1">
                <UIScaleToggle />
              </div>
              <div className="px-2 pb-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Theme</span>
                  <ThemeToggle />
                </div>
              </div>
              <DropdownMenuSeparator />

              {/* Online Users */}
              <div className="px-2 py-1">
                <OnlineUsers />
              </div>
              <DropdownMenuSeparator />

              {/* Navigation */}
              <DropdownMenuItem onClick={() => onTabChange("create-test")}>
                <Plus className="mr-2 h-4 w-4" />
                Create New Test
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTabChange("barcode-generator")}>
                <QrCode className="mr-2 h-4 w-4" />
                Barcode Generator
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTabChange("stool-reports")}>
                <FlaskConical className="mr-2 h-4 w-4" />
                Stool Reports
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTabChange("settings")}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>

              <DropdownMenuItem onClick={() => onTabChange("patients")}>
                <User className="mr-2 h-4 w-4" />
                Patients
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTabChange("subscription")}>
                <CreditCard className="mr-2 h-4 w-4" />
                Subscription
              </DropdownMenuItem>
              {isOwner && (
                <DropdownMenuItem onClick={() => onTabChange("team")}>
                  <Users className="mr-2 h-4 w-4" />
                  Team
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};